import React, { useContext, useEffect, useRef, useState } from "react";
import { Button } from "@mui/material";
import {
  Input,
  MuiBox,
  MuiTypography,
  SubmitLoaderButton,
} from "components/atoms";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { closeCenterModal } from "store/slices/CenterModal.slice";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  CreateFolderAction,
  UpdateFolderAction,
} from "store/middlewares/folders";
import { useNavigate } from "react-router-dom";
import { RenameFilesAction } from "store/middlewares/files";
import { FilterContext } from "context/FilterContext";
import { openToast } from "store/slices/toast.slice";

const CreateRenameModal = ({
  title,
  btnName,
  filetype = "folder",
  id,
  type,
  item,
  clearSelection,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [apiError, setApiError] = useState(null);
  const { filterData } = useContext(FilterContext);
  const inputRef = useRef(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value?.length ?? 0;
      el.setSelectionRange(len, len);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // // Instantly close the rename modal if a socket rename event is received for this item
  // useEffect(() => {
  //   if (type !== "rename" || !item?.id) return;

  //   const handleSocketItemChange = (data) => {
  //     if (!data) return;
  //     const socketType = data?.type;
  //     const payloadData = data?.data;

  //     const targetId =
  //       payloadData?.id ||
  //       payloadData?.file?.id ||
  //       payloadData?.folder?.id ||
  //       payloadData?.fileId ||
  //       payloadData?.folderId;

  //     if (
  //       (socketType === "file_rename" ||
  //         socketType === "folder_update" ||
  //         socketType === "folder_rename") &&
  //       String(targetId) === String(item?.id)
  //     ) {
  //       dispatch(closeCenterModal());
  //     }
  //   };

  //   const socket = getSocket();
  //   if (socket) {
  //     socket.on("item_changed", handleSocketItemChange);
  //   }

  //   return () => {
  //     if (socket) {
  //       socket.off("item_changed", handleSocketItemChange);
  //     }
  //   };
  // }, [type, item?.id, dispatch]);

  const handleSubmit = (values) => {
    setLoader(true);
    if (filetype === "folder") {
      if (type === "rename") {
        dispatch(
          UpdateFolderAction({
            id: item?.id,
            name: values.name.trim(),
            onSuccess: () => {
              setLoader(false);
              dispatch(closeCenterModal());
              clearSelection?.();
            },
            onError: (error) => {
              setLoader(false);
              if (error?.statusCode === 2003) {
                setApiError({ name: values.name, message: t("errorCode.2003") });
              }
            },
          }),
        );
      } else {
        dispatch(
          CreateFolderAction({
            name: values.name.trim(),
            ...(id != null && { parentId: id }),
            sortBy: filterData?.sortBy,
            sortOrder: filterData?.sortOrder,
            onSuccess: () => {
              id == null && navigate("/drive/mydrive");
            },
            onError: (error) => {
              setLoader(false);
              if (error?.statusCode === 2003) {
                setApiError({ name: values.name, message: t("errorCode.2003") });
              }
              if (error?.statusCode === 2001) {
                dispatch(closeCenterModal());
                dispatch(openToast({ message: t("errorCode.2001"), type: "error" }));
              }
              if (error?.statusCode === 1040) {
                dispatch(openToast({ message: t("errorCode.1040"), type: "error" }));
              }
            },
          }),
        );
      }
    } else {
      dispatch(
        RenameFilesAction({
          fileId: item?.id,
          name: values.name.trim(),
          onSuccess: () => {
            setLoader(false);
            dispatch(closeCenterModal());
            clearSelection?.();
          },
          onError: (error) => {
            setLoader(false);
            if (error?.statusCode === 2004) {
              setApiError({ name: values.name, message: t("errorCode.2004") });
            }
          },
        }),
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      name:
        filetype === "folder"
          ? type === "rename"
            ? item?.name ?? ""
            : ""
          : item?.file_name ?? "",
    },
    validationSchema: yup.object({
      name: yup
        .string()
        .trim()
        .required(
          filetype === "file"
            ? t("validation.pleaseEnterFileName")
            : t("validation.pleaseEnterFolderName"),
        ),
    }),
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleSubmit,
  });

  const nameError =
    (formik.touched.name && formik.errors.name) ||
    (apiError?.name === formik.values.name ? apiError.message : undefined);

  return (
    <MuiBox component={"form"} onSubmit={formik.handleSubmit}>
      <MuiBox
        sx={{
          p: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <MuiTypography fontWeight="600" variant="20" color="text.darkBlack">
          {title}
        </MuiTypography>
        <Input
          ref={inputRef}
          isBorderColor={true}
          name={"name"}
          placeholder={t("modal.rename.enterName")}
          maxCharacter={250}
          value={formik.values.name}
          onChange={(event) => {
            setApiError(null);
            formik.handleChange(event);
          }}
          onBlur={formik.handleBlur}
          hideErrorOnFocus={false}
          error={Boolean(nameError)}
          errorMsg={nameError}
          isAlphaNumericWithSpecial
        />
      </MuiBox>
      <MuiBox
        sx={{
          display: "flex",
          gap: "12px",
          justifyContent: "end",
          backgroundColor: "background.gray200",
          p: "16px 24px",
        }}
      >
        <Button
          variant="whiteContained"
          onClick={() => dispatch(closeCenterModal())}
        >
          {t("commonButton.cancel")}
        </Button>
        <SubmitLoaderButton
          variant="blueContained"
          btnText={btnName}
          isDisable={loader}
          isLoading={loader}
          type="submit"
        />
      </MuiBox>
    </MuiBox>
  );
};

export default CreateRenameModal;
